import React, { useRef, useState, useCallback, useEffect } from 'react';
import LeftPanel from './components/LeftPanel';
import Viewport3D, { Viewport3DHandle } from './components/Viewport3D';
import LoginScreen from './components/LoginScreen';
import AdminPanel from './components/AdminPanel';
import LayersDialog from './components/LayersDialog';
import TemplateDesigner from './components/TemplateDesigner';
import ParametersDialog from './components/ParametersDialog';
import TemplatePreviewDialog from './components/TemplatePreviewDialog';
import StartupScreen from './components/StartupScreen';
import TemplateMismatchDialog from './components/TemplateMismatchDialog';
import UnsavedChangesDialog from './components/UnsavedChangesDialog';
import TemplateNormalizerDialog from './components/TemplateNormalizerDialog';
import TemLayersDialog, { TemLayer } from './components/TemLayersDialog';
import { AppState, AppStateUpdate, UserInfo, AuthState, Layer, TemplateInfo, PICTURE_QUALITY_PX, VectorizeResult, TriangulationResult, SimState, MACHINE_TYPES } from './types';
import { computeAndApplyDistortion, buildHeightMapDataUrl } from './utils/distortions';
import { generateMeshSvg } from './utils/meshSvg';
import { estimateMaxBlowHeight } from './utils/blowEstimate';
import { vectorizeTemplate } from './utils/vectorize';
import { loadAuth, clearAuth, saveAuth } from './auth';
import { buildCompositeTexture } from './utils/compositeTexture';
import { readPngInfo } from './utils/pngInfo';
import { saveProject, loadProject } from './utils/projectFile';
import { exportSheetAsObjZip } from './utils/exportObj';
import { exportMeshAsPddm }    from './utils/exportMesh';
import { generateDefaultTemplate, fitTemplateToMaterial } from './utils/templateUtils';
import { Lang, loadLang, saveLang } from './i18n';

const INITIAL_STATE: AppState = {
  materialWidth:      300,
  materialHeight:     300,
  materialColor:      '#1e22aa',
  maxBlowHeight:      144,
  pressureFraction:   1.0,
  currentHeight:      0.0,
  showGridCm:         false,
  showGridMm:         false,
  showMargin:         false,
  negGMColor:         false,
  pullUpMaxH:         true,
  showComputed:       false,
  showVisible:        false,
  showFullMaterial:   false,
  currentOpening:     1,
  totalOpenings:      1,
  marginMm:           15,
  thicknessMm:        1.0,
  opMarginMm:         0.0,
  transparency:       50,
  materialAboveImage: false,
  multipleContours:   true,
  pictureQuality:     'Normal',
  gridDensity:        'Normal',
  machineType:        1,
  previewTexturePx:   1024,
  distortionVersion:  1,
  distortionStrength: 1.0,
  // FEM solver tuning – wartości domyślne
  femRampLevels:        100,
  femRampCheckPeriod:   400,
  femBendingFactor:     0.0,
  femBoostFactor:       1.05,
  femMaterialFailRatio: 8.0,
};


const App: React.FC = () => {
  // ── Autoryzacja – stan inicjalizowany z localStorage ──────
  const [auth, setAuth] = useState<AuthState>(() => loadAuth());
  const [lang, setLang] = useState<Lang>(() => loadLang());

  const handleLangChange = (l: Lang) => { saveLang(l); setLang(l); };

  // ── Stan aplikacji PDD ────────────────────────────────────
  const [appState, setAppState]         = useState<AppState>(INITIAL_STATE);
  const [layers, setLayers]             = useState<Layer[]>([]);
  const [showLayers, setShowLayers]           = useState(false);
  const [showTemLayers, setShowTemLayers]     = useState(false);
  const [temLayers, setTemLayers]             = useState<TemLayer[]>([]);
  const [showTplDesigner, setShowTplDesigner] = useState(false);
  const [showParameters, setShowParameters]   = useState(false);
  const [template, setTemplate]               = useState<TemplateInfo | null>(null);
  const [infoMsg, setInfoMsg]                 = useState<string | null>(null);
  const [textureDataUrl, setTextureDataUrl]           = useState<string | null>(null);
  const [distortedTextureDataUrl, setDistortedTextureDataUrl] = useState<string | null>(null);
  const [vectorize, setVectorize]             = useState<VectorizeResult | null>(null);
  const [triangulation, setTriangulation]     = useState<TriangulationResult | null>(null);
  const [suggestedMaxH, setSuggestedMaxH]     = useState<number | null>(null);
  const [meshSvg, setMeshSvg]                 = useState<string | null>(null);
  const [showTplPreview, setShowTplPreview]   = useState(false);
  const [meshStatus, setMeshStatus]           = useState<'idle'|'vectorizing'|'triangulating'|'done'|'error'>('idle');
  const [meshError,  setMeshError]            = useState<string | null>(null);

  // ── Stan projektu / startup ───────────────────────────────────
  const [projectOpen, setProjectOpen]             = useState(false);
  const [showStartupParams, setShowStartupParams] = useState(false);
  const [showNormalizer, setShowNormalizer]       = useState(false);
  const [normInitTpl, setNormInitTpl]             = useState<{ dataUrl: string; widthMm: number; heightMm: number } | null>(null);
  const [templateMismatch, setTemplateMismatch]   = useState<{
    dataUrl: string; widthMm: number; heightMm: number;
  } | null>(null);

  // ── Śledzenie niezapisanych zmian ─────────────────────────────
  const [isDirty, setIsDirty]       = useState(false);
  const [showUnsaved, setShowUnsaved] = useState(false);
  const pendingActionRef = useRef<(() => void) | null>(null);

  // Klucze AppState, których zmiana NIE oznacza brudnego projektu
  // (opcje wyświetlania, nawigacja otworów, aktualny stan symulacji)
  const DISPLAY_ONLY_KEYS = new Set<keyof AppState>([
    'showGridCm', 'showGridMm', 'showMargin', 'negGMColor',
    'showComputed', 'showVisible', 'showFullMaterial', 'currentOpening', 'currentHeight',
  ]);

  // ── Stan symulacji ────────────────────────────────────────────
  const [simState,        setSimState]        = useState<SimState>('idle');
  const [simSessionId,    setSimSessionId]    = useState<string | null>(null);
  const [simZPositionsMm, setSimZPositionsMm] = useState<number[] | null>(null);
  const [simXPositionsMm, setSimXPositionsMm] = useState<number[] | null>(null);
  const [simYPositionsMm, setSimYPositionsMm] = useState<number[] | null>(null);
  const simEsRef    = useRef<EventSource | null>(null);
  const simSessRef  = useRef<string | null>(null);   // zsynchronizowany z simSessionId
  // Diagnostyka symulacji – wiersze CSV (nagłówek + dane)
  const simDiagRef  = useRef<string[]>([]);          // nie powoduje re-renderu
  const [simDiagReady, setSimDiagReady] = useState(false);

  // Ostatnie stosunki powierzchni trójkątów z symulacji (potrzebne do Distortions)
  const areaRatiosRef = useRef<number[] | null>(null);
  // Ostatnie pozycje Z/X/Y wierzchołków siatki z symulacji (potrzebne do Distortions)
  const simZPositionsMmRef = useRef<number[] | null>(null);
  const simXPositionsMmRef = useRef<number[] | null>(null);
  const simYPositionsMmRef = useRef<number[] | null>(null);
  // Lista otworów, dla których deformacja została zastosowana (zachowana kolejność)
  // { openingIdx, strength } – potrzebne do re-aplikacji przy reset jednego otworu
  const appliedDistortionsRef = useRef<{ openingIdx: number; strength: number }[]>([]);
  // Oryginalna tekstura (przed jakimikolwiek deformacjami) – potrzebna do re-aplikacji
  const originalTextureRef = useRef<string | null>(null);

  const viewportRef    = useRef<Viewport3DHandle>(null);
  const tplFileRef     = useRef<HTMLInputElement>(null);
  const projFileRef    = useRef<HTMLInputElement>(null);

  // Generuj teksturę za każdym razem, gdy zmienią się warstwy / kolor / szablon
  useEffect(() => {
    let cancelled = false;
    buildCompositeTexture(
      layers,
      appState.materialColor,
      appState.materialWidth,
      appState.materialHeight,
      template,
      appState.previewTexturePx,
    ).then(canvas => {
      if (!cancelled) setTextureDataUrl(canvas.toDataURL());
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [layers, appState.materialColor, appState.materialWidth, appState.materialHeight, template, appState.previewTexturePx]);

  // Gdy zmienia się bazowa tekstura, usuń stare pre-deformacje
  useEffect(() => {
    setDistortedTextureDataUrl(null);
    appliedDistortionsRef.current = [];
    originalTextureRef.current = null;
  }, [textureDataUrl]);

  // Wektoryzacja konturów szablonu (po stronie klienta – Canvas 2D)
  useEffect(() => {
    if (!template) {
      setVectorize(null);
      setMeshStatus('idle');
      setMeshError(null);
      return;
    }
    let cancelled = false;
    setMeshStatus('vectorizing');
    setMeshError(null);
    setVectorize(null);
    setTriangulation(null);
    setMeshSvg(null);

    vectorizeTemplate(template.dataUrl, template.widthMm, template.heightMm)
      .then(data => {
        if (cancelled) return;
        setVectorize(data);
        setInfoMsg(prev =>
          prev
            ? `${prev.split('·')[0].trim()} · ${data.contours.length} contour(s)`
            : `${data.contours.length} contour(s)`,
        );
      })
      .catch(err => {
        if (cancelled) return;
        const msg = `Vectorize error: ${err.message}`;
        setMeshStatus('error');
        setMeshError(msg);
        setInfoMsg(prev => prev ? `${prev.split('·')[0].trim()} · ⚠ ${msg}` : `⚠ ${msg}`);
      });

    return () => { cancelled = true; };
  }, [template]);

  // Triangulacja siatki MES po zakończeniu wektoryzacji
  useEffect(() => {
    if (!vectorize || !template || !auth.token) return;
    let cancelled = false;
    setMeshStatus('triangulating');
    setMeshError(null);

    // maxAreaMm2 skaluje się z powierzchnią arkusza, żeby docelowa liczba trójkątów
    // była stała niezależnie od fizycznych wymiarów materiału.
    const DENSITY_TRIANGLES: Record<string, number> = { Low: 600, Normal: 1200, High: 2400, VeryHigh: 4800 };
    const targetTris = DENSITY_TRIANGLES[appState.gridDensity] ?? 1200;
    const sheetArea  = appState.materialWidth * appState.materialHeight;
    const maxAreaMm2 = Math.max(1, Math.min(500, sheetArea / targetTris));

    fetch('/api/triangulate', {
      method:  'POST',
      headers: {
        'Content-Type':  'application/json',
        'Authorization': `Bearer ${auth.token}`,
      },
      body: JSON.stringify({
        contours:             vectorize.contours,
        cutContours:          vectorize.cutContours,
        cutContoursInverted:  vectorize.cutContoursInverted,
        outerBoundary:        vectorize.outerBoundary,
        widthMm:              template.widthMm,
        heightMm:             template.heightMm,
        maxAreaMm2,
      }),
    })
      .then(r => r.ok ? r.json() : r.json().then(d => { throw new Error(d.error ?? `HTTP ${r.status}`); }))
      .then((data: TriangulationResult & { error?: string }) => {
        if (cancelled) return;
        if (data.error) throw new Error(data.error);

        // Nowa triangulacja ma inną topologię węzłów — pozycje z poprzedniej symulacji
        // są niekompatybilne i spowodowałyby chaotyczny wygląd siatki 3D.
        // Czyścimy stan symulacji PRZED zastosowaniem nowej geometrii, żeby
        // Viewport3D nie re-aplikował starych pozycji na nowej siatce.
        viewportRef.current?.resetSimPositions();
        areaRatiosRef.current      = null;
        simZPositionsMmRef.current = null;
        simXPositionsMmRef.current = null;
        simYPositionsMmRef.current = null;
        setSimZPositionsMm(null);
        simDiagRef.current = [];
        setSimDiagReady(false);
        if (simSessRef.current) {
          const staleId = simSessRef.current;
          if (simEsRef.current) { simEsRef.current.close(); simEsRef.current = null; }
          simSessRef.current = null;
          setSimSessionId(null);
          setSimState('idle');
          // Poinformuj serwer (fire-and-forget — nie blokuj)
          if (auth.token) {
            fetch(`/api/simulate/stop/${staleId}`, {
              method:  'POST',
              headers: { 'Authorization': `Bearer ${auth.token}` },
            }).catch(() => {});
          }
        }

        setTriangulation(data);
        setMeshSvg(generateMeshSvg(data, template.widthMm, template.heightMm));
        const suggested = Math.round(estimateMaxBlowHeight(data));
        setSuggestedMaxH(suggested);
        setAppState(prev => ({
          ...prev,
          maxBlowHeight:  suggested,
          totalOpenings:  data.stats.openingCount,
          currentOpening: Math.min(prev.currentOpening, data.stats.openingCount),
        }));
        setMeshStatus('done');
        setInfoMsg(prev => {
          const base = prev ? prev.split(' |')[0].trim() : '';
          return `${base} | mesh: ${data.stats.vertexCount} nodes, ${data.stats.triangleCount} tris`;
        });
      })
      .catch(err => {
        if (cancelled) return;
        const msg = `Triangulate error: ${err.message}`;
        setMeshStatus('error');
        setMeshError(msg);
        setInfoMsg(prev => prev ? `${prev.split(' |')[0].trim()} | ⚠ ${msg}` : `⚠ ${msg}`);
      });
    return () => { cancelled = true; };
  }, [vectorize, template, auth.token, appState.gridDensity, appState.materialWidth, appState.materialHeight]);

  // ── Handlery autoryzacji ──────────────────────────────────
  const handleLogin = useCallback((user: UserInfo, token: string) => {
    saveAuth(token, user);
    setAuth({ user, token });
  }, []);

  const handleLogout = useCallback(() => {
    // Powiadom serwer o wylogowaniu (na potrzeby logu aktywności); fire-and-forget
    const token = auth.token;
    if (token) {
      fetch('/api/auth/logout', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    }
    clearAuth();
    setAuth({ user: null, token: null });
  }, [auth.token]);

  // ── Aktualizacja stanu PDD ────────────────────────────────
  const handleUpdate = useCallback((update: AppStateUpdate) => {
    setAppState(prev => ({ ...prev, ...update }));
    // Zmiana pola "projektowego" (nie tylko wyświetlania) → projekt brudny
    const hasMeaningfulChange = Object.keys(update).some(
      k => !DISPLAY_ONLY_KEYS.has(k as keyof AppState),
    );
    if (hasMeaningfulChange) setIsDirty(true);
  }, []); // DISPLAY_ONLY_KEYS jest stałą (zawsze te same wartości), setIsDirty jest stabilny

  // ── Helpery zarządzania połączeniem SSE ──────────────────────

  const closeSse = useCallback(() => {
    if (simEsRef.current) {
      simEsRef.current.close();
      simEsRef.current = null;
    }
  }, []);

  // ── Zatrzymanie symulacji przed zmianą siatki ─────────────
  const stopSimIfRunning = useCallback(async () => {
    const id = simSessRef.current;
    if (!id) return;
    closeSse();
    try {
      await fetch(`/api/simulate/stop/${id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${auth.token}` },
      });
    } catch { /* ignore */ }
    simSessRef.current = null;
    setSimSessionId(null);
    setSimState('idle');
    simZPositionsMmRef.current = null;
    setSimZPositionsMm(null);
    viewportRef.current?.resetSimPositions();
    setAppState(prev => ({ ...prev, currentHeight: 0 }));
  }, [auth.token, closeSse]);

  // ── Obsługa niezapisanych zmian ───────────────────────────

  // Jeśli projekt jest brudny, pokazuje dialog z pytaniem o zapis,
  // w przeciwnym razie wywołuje action od razu.
  const withDirtyCheck = useCallback((action: () => void) => {
    if (!isDirty) { action(); return; }
    pendingActionRef.current = action;
    setShowUnsaved(true);
  }, [isDirty]);

  const handleUnsavedSave = async () => {
    try {
      await saveProject(appState, layers, template);
      setIsDirty(false);
      setShowUnsaved(false);
      const action = pendingActionRef.current;
      pendingActionRef.current = null;
      action?.();
    } catch (err: any) {
      alert(`Save failed: ${err.message}`);
      setShowUnsaved(false);
      pendingActionRef.current = null;
    }
  };

  const handleUnsavedDiscard = () => {
    setShowUnsaved(false);
    setIsDirty(false);
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    action?.();
  };

  const handleUnsavedCancel = () => {
    setShowUnsaved(false);
    pendingActionRef.current = null;
  };

  // ── Handlery przycisków (stubs) ───────────────────────────
  const handleLoadTemplate    = () => tplFileRef.current?.click();

  const handleUseTemplateFromNormalizer = async (dataUrl: string, widthMm: number, heightMm: number) => {
    await stopSimIfRunning();
    const matW = appState.materialWidth;
    const matH = appState.materialHeight;
    if (widthMm > 0 && heightMm > 0) {
      if (widthMm > matW || heightMm > matH) {
        setTemplateMismatch({ dataUrl, widthMm, heightMm });
        return;
      } else if (widthMm < matW && heightMm < matH) {
        const fitted = await fitTemplateToMaterial(dataUrl, widthMm, heightMm, matW, matH, false);
        setTemplate({ dataUrl: fitted.dataUrl, widthMm: fitted.widthMm, heightMm: fitted.heightMm });
        setInfoMsg(`Template: ${widthMm.toFixed(1)} × ${heightMm.toFixed(1)} mm (fitted to material)`);
      } else {
        setTemplate({ dataUrl, widthMm, heightMm });
        setInfoMsg(`Template: ${widthMm.toFixed(1)} × ${heightMm.toFixed(1)} mm`);
      }
    } else {
      setTemplate({ dataUrl, widthMm: matW, heightMm: matH });
      setInfoMsg(`Template loaded (no mm info – assumed ${matW} × ${matH} mm)`);
    }
    setIsDirty(true);
  };
  const handleTemplateFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    await stopSimIfRunning();

    const buf   = await file.arrayBuffer();
    const bytes = new Uint8Array(buf);
    const info  = readPngInfo(bytes);

    const dataUrl = await new Promise<string>(res => {
      const r = new FileReader();
      r.onload = ev => res(ev.target!.result as string);
      r.readAsDataURL(file);
    });

    let msg: string;
    if (info.pixelsPerMetre && info.pixelsPerMetre > 0) {
      const wMm = Math.round(info.widthPx  * 1000 / info.pixelsPerMetre * 10) / 10;
      const hMm = Math.round(info.heightPx * 1000 / info.pixelsPerMetre * 10) / 10;
      const matW = appState.materialWidth;
      const matH = appState.materialHeight;

      if (wMm > matW || hMm > matH) {
        // Szablon większy od materiału – pytaj użytkownika
        setTemplateMismatch({ dataUrl, widthMm: wMm, heightMm: hMm });
        return;
      } else if (wMm < matW && hMm < matH) {
        // Szablon mniejszy – dopasuj (padding czarny, centrowanie)
        const fitted = await fitTemplateToMaterial(dataUrl, wMm, hMm, matW, matH, false);
        setTemplate({ dataUrl: fitted.dataUrl, widthMm: fitted.widthMm, heightMm: fitted.heightMm });
        msg = `Template: ${wMm.toFixed(1)} × ${hMm.toFixed(1)} mm (fitted to material)`;
      } else {
        // Exact match
        setTemplate({ dataUrl, widthMm: wMm, heightMm: hMm });
        msg = `Template: ${wMm.toFixed(1)} × ${hMm.toFixed(1)} mm`;
      }
    } else {
      // Brak DPI – użyj rozmiarów materiału jako domyślnych
      setTemplate({ dataUrl, widthMm: appState.materialWidth, heightMm: appState.materialHeight });
      msg = `Template loaded (no DPI – assumed ${appState.materialWidth} × ${appState.materialHeight} mm)`;
    }
    setInfoMsg(msg);
    setIsDirty(true);
  };

  const executeNewProj = useCallback(async () => {
    await stopSimIfRunning();
    setAppState(INITIAL_STATE);
    setLayers([]);
    setTemplate(null);
    setInfoMsg(null);
    setVectorize(null);
    setTriangulation(null);
    setSuggestedMaxH(null);
    setMeshSvg(null);
    setMeshStatus('idle');
    setMeshError(null);
    setIsDirty(false);
    setProjectOpen(false);
  }, [stopSimIfRunning]);

  const handleNewProj = () => withDirtyCheck(() => executeNewProj().catch(() => {}));

  const handleSaveProj = () =>
    saveProject(appState, layers, template)
      .then(() => setIsDirty(false))
      .catch(err => alert(`Save failed: ${err.message}`));

  const executeExit = useCallback(async () => {
    await stopSimIfRunning();
    setIsDirty(false);
    setProjectOpen(false);
  }, [stopSimIfRunning]);

  const handleExit = () => withDirtyCheck(() => executeExit().catch(() => {}));

  const handleLoadProj = () => withDirtyCheck(() => projFileRef.current?.click());

  const handleProjFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    await stopSimIfRunning();
    try {
      const loaded = await loadProject(file, INITIAL_STATE);
      setAppState(loaded.appState);
      setLayers(loaded.layers);
      setTemplate(loaded.template);
      if (loaded.template) {
        setInfoMsg(`Template: ${loaded.template.widthMm.toFixed(1)} × ${loaded.template.heightMm.toFixed(1)} mm`);
      } else {
        setInfoMsg(null);
      }
      setIsDirty(false);
      setProjectOpen(true);
    } catch (err: any) {
      alert(`Load failed: ${err.message}`);
    }
  };
  const handleLayers          = () => setShowLayers(true);
  const handleTemLayers       = () => setShowTemLayers(true);
  const handleTempDesign      = () => setShowTplDesigner(true);
  const handleParameters      = () => setShowParameters(true);
  const handleResetView       = () => viewportRef.current?.resetView();
  /**
   * Buduje pełnowymiarową teksturę eksportową:
   * 1. buildCompositeTexture w jakości eksportowej (longPx)
   * 2. Sekwencyjnie nakłada zapisane deformacje z appliedDistortionsRef
   * Dzięki temu Save 3D i Save 2D uwzględniają Distortions tak samo jak podgląd 3D.
   */
  const buildExportTexture = async (longPx: number): Promise<string | null> => {
    let url: string | null = null;
    try {
      const canvas = await buildCompositeTexture(
        layers, appState.materialColor,
        appState.materialWidth, appState.materialHeight,
        template, longPx,
      );
      url = canvas.toDataURL();
    } catch {
      url = textureDataUrl; // fallback na podgląd
    }
    const distortions = appliedDistortionsRef.current;
    if (distortions.length > 0 && triangulation && url) {
      for (const { openingIdx, strength } of distortions) {
        url = await computeAndApplyDistortion(
          url, triangulation, openingIdx,
          areaRatiosRef.current, simZPositionsMmRef.current,
          appState.materialWidth, appState.materialHeight, strength,
          simXPositionsMmRef.current, simYPositionsMmRef.current,
        );
      }
    }
    return url;
  };

  const handleSave3D = async () => {
    if (!triangulation) {
      alert('Brak siatki triangulacji – najpierw wczytaj szablon.');
      return;
    }
    const exportLongPx    = PICTURE_QUALITY_PX[appState.pictureQuality];
    const exportTexDataUrl = await buildExportTexture(exportLongPx);
    exportSheetAsObjZip(triangulation, appState, exportTexDataUrl,
      simZPositionsMm, simXPositionsMm, simYPositionsMm)
      .catch(err => alert(`Błąd eksportu OBJ: ${err.message}`));
  };
  const handleSave2D = async () => {
    if (layers.length === 0) { alert('Brak warstw do eksportu.'); return; }
    const size = PICTURE_QUALITY_PX[appState.pictureQuality];

    // Jeśli są aktywne deformacje – użyj pełnowymiarowego compositu z deformacjami
    if (appliedDistortionsRef.current.length > 0) {
      const url = await buildExportTexture(size);
      if (url) {
        const a = document.createElement('a');
        a.download = 'composite.png';
        a.href = url;
        a.click();
        return;
      }
    }

    // Brak deformacji – oryginalne zachowanie: rysuj warstwy ręcznie
    const CANVAS_SIZE = 260;  // rozmiar canvasu podglądu (z LayersDialog)
    const drawScale   = size / CANVAS_SIZE;
    const cx = size / 2, cy = size / 2;

    const imgs = await Promise.all(
      layers.map(l => new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.onload  = () => resolve(img);
        img.onerror = reject;
        img.src = l.imageDataUrl;
      })),
    );

    const off = document.createElement('canvas');
    off.width = size; off.height = size;
    const ctx = off.getContext('2d')!;

    for (let i = layers.length - 1; i >= 0; i--) {
      const l   = layers[i];
      const img = imgs[i];
      ctx.save();
      ctx.translate(cx + l.translateX * drawScale, cy + l.translateY * drawScale);
      ctx.rotate((l.rotate * Math.PI) / 180);
      const dw = img.naturalWidth  * l.baseScale * l.scale * drawScale;
      const dh = img.naturalHeight * l.baseScale * l.scale * drawScale;
      ctx.drawImage(img, -dw / 2, -dh / 2, dw, dh);
      ctx.restore();
    }

    const a = document.createElement('a');
    a.download = 'composite.png';
    a.href = off.toDataURL('image/png');
    a.click();
  };
  const handleDistortionReset = useCallback(async () => {
    const openingIdx = appState.currentOpening;
    const remaining  = appliedDistortionsRef.current.filter(d => d.openingIdx !== openingIdx);
    appliedDistortionsRef.current = remaining;

    if (remaining.length === 0) {
      // Wszystkie deformacje usunięte
      setDistortedTextureDataUrl(null);
      originalTextureRef.current = null;
      return;
    }

    // Re-aplikuj pozostałe deformacje od oryginału
    const orig = originalTextureRef.current;
    const tri  = triangulation;
    if (!orig || !tri) {
      setDistortedTextureDataUrl(null);
      originalTextureRef.current = null;
      return;
    }
    try {
      let result = orig;
      for (const { openingIdx: oi, strength: s } of remaining) {
        result = await computeAndApplyDistortion(
          result, tri, oi,
          areaRatiosRef.current, simZPositionsMmRef.current,
          appState.materialWidth, appState.materialHeight, s,
          simXPositionsMmRef.current, simYPositionsMmRef.current,
        );
      }
      setDistortedTextureDataUrl(result);
    } catch {
      setDistortedTextureDataUrl(null);
      originalTextureRef.current = null;
      appliedDistortionsRef.current = [];
    }
  }, [appState.currentOpening, appState.materialWidth, appState.materialHeight, triangulation]);

  const handleDistortions = useCallback(async () => {
    const tri        = triangulation;
    const openingIdx = appState.currentOpening;

    if (!tri) {
      alert('Brak siatki triangulacji – najpierw wczytaj szablon.');
      return;
    }
    if (!textureDataUrl) {
      alert('Brak tekstury – najpierw dodaj warstwy.');
      return;
    }
    if (openingIdx < 1 || openingIdx > tri.stats.openingCount) {
      alert(`Otwór ${openingIdx} nie istnieje w siatce.`);
      return;
    }

    // Zachowaj oryginał przy pierwszej deformacji
    if (!originalTextureRef.current) {
      originalTextureRef.current = textureDataUrl;
    }

    // Usuń poprzednią deformację dla tego otworu (jeśli istniała),
    // a następnie przelicz od nowa z aktualnymi parametrami.
    const remaining = appliedDistortionsRef.current.filter(d => d.openingIdx !== openingIdx);

    try {
      // Odtwórz bazową teksturę: oryginał + pozostałe otwory (bez bieżącego)
      let base = originalTextureRef.current;
      for (const { openingIdx: oi, strength: s } of remaining) {
        base = await computeAndApplyDistortion(
          base, tri, oi,
          areaRatiosRef.current, simZPositionsMmRef.current,
          appState.materialWidth, appState.materialHeight, s,
          simXPositionsMmRef.current, simYPositionsMmRef.current,
        );
      }

      // Aplikuj deformację dla bieżącego otworu z aktualnymi parametrami
      const result = await computeAndApplyDistortion(
        base, tri, openingIdx,
        areaRatiosRef.current, simZPositionsMmRef.current,
        appState.materialWidth, appState.materialHeight,
        appState.distortionStrength,
        simXPositionsMmRef.current, simYPositionsMmRef.current,
      );

      appliedDistortionsRef.current = [
        ...remaining,
        { openingIdx, strength: appState.distortionStrength },
      ];
      setDistortedTextureDataUrl(result);
    } catch (err: any) {
      alert(`Błąd deformacji: ${err.message}`);
    }
  }, [
    triangulation, textureDataUrl,
    appState.currentOpening, appState.materialWidth, appState.materialHeight,
    appState.distortionStrength,
  ]);

  const openSse = useCallback((sessionId: string, token: string) => {
    closeSse();
    // EventSource nie obsługuje nagłówków – przekazujemy token jako query param
    const es = new EventSource(
      `/api/simulate/stream/${sessionId}?token=${encodeURIComponent(token)}`,
    );
    simEsRef.current = es;

    // Nagłówek CSV diagnostyki
    simDiagRef.current = [
      'tick,stepCount,maxHeightMm,avgXYDispMm,maxXYDispMm,avgAreaRatio,maxAreaRatio,minAreaRatio',
    ];
    setSimDiagReady(false);

    es.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);

        // Aktualizacja pozycji siatki (update, diag, done, converged, stopped)
        if (data.zPosMm) {
          // Po zbieżności skalujemy Z tak, żeby szczyt kopuły trafił dokładnie w maxBlowHeight.
          // Podczas animacji pokazujemy naturalną fizykę (zScale=1).
          const isFinalEvent = (data.type === 'done' || data.type === 'converged')
            && appState.pullUpMaxH;
          const naturalH     = data.maxHeightMm as number;
          const zScale       = isFinalEvent && naturalH > 0
            ? appState.maxBlowHeight / naturalH
            : 1;
          const displayZ: number[] = zScale !== 1
            ? (data.zPosMm as number[]).map((z: number) => z * zScale)
            : data.zPosMm as number[];
          const displayH = isFinalEvent ? appState.maxBlowHeight : (naturalH ?? 0);
          viewportRef.current?.updateSimPositions(
            data.xPosMm ?? [],
            data.yPosMm ?? [],
            displayZ,
          );
          simZPositionsMmRef.current = displayZ;
          simXPositionsMmRef.current = data.xPosMm ? Array.from(data.xPosMm as number[]) : null;
          simYPositionsMmRef.current = data.yPosMm ? Array.from(data.yPosMm as number[]) : null;
          setSimZPositionsMm(displayZ);
          setSimXPositionsMm(data.xPosMm ? Array.from(data.xPosMm as number[]) : null);
          setSimYPositionsMm(data.yPosMm ? Array.from(data.yPosMm as number[]) : null);
          setAppState(prev => ({ ...prev, currentHeight: displayH }));
        }

        // Wiersz diagnostyczny
        if (data.type === 'diag') {
          const row = [
            data.tick, data.stepCount, data.maxHeightMm,
            data.avgXYDispMm, data.maxXYDispMm,
            data.avgAreaRatio, data.maxAreaRatio, data.minAreaRatio,
          ].join(',');
          simDiagRef.current.push(row);
        }

        if (data.type === 'done' || data.type === 'stopped' || data.type === 'converged') {
          // Zapisz stosunki powierzchni (potrzebne do Distortions)
          if (data.areaRatios) {
            areaRatiosRef.current = Array.from(data.areaRatios as number[]);
          }
          // Ostatni wiersz diagnostyczny przy zakończeniu
          if (data.stepCount != null) {
            const row = [
              'final', data.stepCount,
              data.maxHeightMm ?? '',
              '', '', '', '', '',
            ].join(',');
            simDiagRef.current.push(row);
          }
          setSimDiagReady(true);
          setSimState('done');
          if (data.type === 'converged') {
            const h = appState.maxBlowHeight != null ? ` (H = ${appState.maxBlowHeight.toFixed(1)} mm)` : '';
            setInfoMsg(prev => {
              const base = prev ? prev.split(' |')[0].trim() : '';
              return `${base} | ✓ Stabilizacja${h}`;
            });
            setTimeout(() => setInfoMsg(prev =>
              prev ? prev.split(' |')[0].trim() || null : null
            ), 2000);
          }
          closeSse();
        }
      } catch { /* ignore */ }
    };
    es.onerror = () => {
      setSimState('error');
      closeSse();
    };
  }, [closeSse, appState.maxBlowHeight, appState.pullUpMaxH]);

  // ── Handlery symulacji ────────────────────────────────────────

  const handleSimStart = useCallback(async () => {
    if (!auth.token || !triangulation) {
      alert('Brak siatki triangulacji – najpierw wczytaj szablon.');
      return;
    }

    // Jeśli symulacja jest zapauzowana – wznów istniejącą sesję
    if (simState === 'paused' && simSessRef.current) {
      try {
        await fetch(`/api/simulate/resume/${simSessRef.current}`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${auth.token}` },
        });
        setSimState('running');
      } catch (err: any) {
        alert(`Błąd wznowienia symulacji: ${err.message}`);
      }
      return;
    }

    // Zatrzymaj poprzednią sesję jeśli istnieje
    if (simSessRef.current) {
      try {
        await fetch(`/api/simulate/stop/${simSessRef.current}`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${auth.token}` },
        });
      } catch { /* ignore */ }
      closeSse();
    }
    viewportRef.current?.resetSimPositions();
    setSimZPositionsMm(null);
    setAppState(prev => ({ ...prev, currentHeight: 0 }));

    try {
      const res = await fetch('/api/simulate/start', {
        method:  'POST',
        headers: {
          'Content-Type':  'application/json',
          'Authorization': `Bearer ${auth.token}`,
        },
        body: JSON.stringify({
          vertices:    triangulation.vertices,
          triangles:   triangulation.triangles,
          triLabels:   triangulation.triLabels,
          maxHeightMm:      appState.maxBlowHeight,
          thicknessMm:      appState.thicknessMm,
          pressureFraction: appState.pressureFraction,
          femRampLevels:        appState.femRampLevels,
          femRampCheckPeriod:   appState.femRampCheckPeriod,
          femBendingFactor:     appState.femBendingFactor,
          femBoostFactor:       appState.femBoostFactor,
          femMaterialFailRatio: appState.femMaterialFailRatio,
          // pressure pominięte → serwer oblicza adaptacyjnie z rozmiaru otworu
        }),
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.error ?? `HTTP ${res.status}`);
      }
      const { sessionId } = await res.json();
      simSessRef.current = sessionId;
      setSimSessionId(sessionId);
      setSimState('running');
      openSse(sessionId, auth.token);
    } catch (err: any) {
      alert(`Błąd uruchomienia symulacji: ${err.message}`);
    }
  }, [auth.token, triangulation, appState.maxBlowHeight, closeSse, openSse, simState]);

  const handleSimPause = useCallback(async () => {
    const id = simSessRef.current;
    if (!id || !auth.token) return;
    try {
      await fetch(`/api/simulate/pause/${id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${auth.token}` },
      });
      setSimState('paused');
    } catch { /* ignore */ }
  }, [auth.token]);

  const handleSimStop = useCallback(async () => {
    const id = simSessRef.current;
    if (!id || !auth.token) return;
    closeSse();
    try {
      const resp = await fetch(`/api/simulate/stop/${id}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${auth.token}` },
      });
      if (resp.ok) {
        const data = await resp.json();
        if (data.areaRatios) {
          areaRatiosRef.current = Array.from(data.areaRatios as number[]);
        }
        if (data.zPosMm) {
          simZPositionsMmRef.current = Array.from(data.zPosMm as number[]);
        }
      }
    } catch { /* ignore */ }
    simSessRef.current = null;
    setSimSessionId(null);
    setSimState('idle');
  }, [auth.token, closeSse]);

  const handleSimReset = useCallback(async () => {
    // Zatrzymaj jeśli działa
    const id = simSessRef.current;
    if (id && auth.token) {
      closeSse();
      try {
        await fetch(`/api/simulate/stop/${id}`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${auth.token}` },
        });
      } catch { /* ignore */ }
      simSessRef.current = null;
      setSimSessionId(null);
    }
    viewportRef.current?.resetSimPositions();
    simZPositionsMmRef.current = null;
    setSimZPositionsMm(null);
    setSimXPositionsMm(null);
    setSimYPositionsMm(null);
    setAppState(prev => ({ ...prev, currentHeight: 0 }));
    setSimState('idle');
    simDiagRef.current = [];
    setSimDiagReady(false);
    areaRatiosRef.current      = null;
    simXPositionsMmRef.current = null;
    simYPositionsMmRef.current = null;
  }, [auth.token, closeSse]);

  const handleDownloadHeightMap = useCallback(() => {
    if (!triangulation) return;
    const dataUrl = buildHeightMapDataUrl(
      triangulation,
      appState.currentOpening,
      simZPositionsMmRef.current,
      appState.materialWidth,
      appState.materialHeight,
    );
    const a = document.createElement('a');
    a.href     = dataUrl;
    a.download = `height_map_op${appState.currentOpening}.png`;
    a.click();
  }, [triangulation, appState.currentOpening, appState.materialWidth, appState.materialHeight]);

  const handleDownloadMesh = useCallback(() => {
    if (!triangulation) return;
    exportMeshAsPddm(triangulation, appState);
  }, [triangulation, appState]);

  const handleDownloadSimLog = useCallback(() => {
    const rows = simDiagRef.current;
    if (!rows.length) return;
    const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = 'sim_log.csv';
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  const handleSimRelax = useCallback(async () => {
    const id = simSessRef.current;
    if (!id || !auth.token) return;
    try {
      await fetch(`/api/simulate/relax/${id}`, {
        method:  'POST',
        headers: { 'Authorization': `Bearer ${auth.token}` },
      });
      setSimState('running');
      // (Ponownie) otwórz SSE – w trybie relaksacji potrzebujemy strumienia aktualizacji
      openSse(id, auth.token);
    } catch (err: any) {
      alert(`Błąd relaksacji: ${err.message}`);
    }
  }, [auth.token, openSse]);

  // ── Routing ───────────────────────────────────────────────

  const handleStartNewProject = () => setShowStartupParams(true);

  // 1. Niezalogowany → ekran logowania
  if (!auth.user || !auth.token) {
    return <LoginScreen onLogin={handleLogin} lang={lang} onLangChange={handleLangChange} />;
  }

  // 2. Administrator → panel zarządzania użytkownikami
  if (auth.user.isAdmin) {
    return (
      <AdminPanel
        currentUser={auth.user}
        token={auth.token}
        onLogout={handleLogout}
      />
    );
  }

  // 3. Startup screen (nowy lub załadowany projekt)
  if (!projectOpen) {
    return (
      <>
        <StartupScreen
          lang={lang}
          onLangChange={handleLangChange}
          onNewProject={handleStartNewProject}
          onLoadProject={() => projFileRef.current?.click()}
          onLogout={handleLogout}
        />
        {showStartupParams && (
          <ParametersDialog
            appState={appState}
            onSave={(update) => {
              setAppState(prev => ({ ...prev, ...update }));
              const newW = update.materialWidth  ?? appState.materialWidth;
              const newH = update.materialHeight ?? appState.materialHeight;
              const tplDataUrl = generateDefaultTemplate(newW, newH);
              setTemplate({ dataUrl: tplDataUrl, widthMm: newW, heightMm: newH });
              setIsDirty(false);
              setProjectOpen(true);
              setShowStartupParams(false);
            }}
            onClose={() => setShowStartupParams(false)}
          />
        )}
        <input ref={projFileRef} type="file" accept=".pdd,.zip"
          style={{ display: 'none' }} onChange={handleProjFile} />
      </>
    );
  }

  // 4. Zwykły użytkownik → główne okno PDD
  return (
    <>
    {showUnsaved && (
      <UnsavedChangesDialog
        onSave={handleUnsavedSave}
        onDiscard={handleUnsavedDiscard}
        onCancel={handleUnsavedCancel}
      />
    )}
    {templateMismatch && (
      <TemplateMismatchDialog
        tplWmm={templateMismatch.widthMm}
        tplHmm={templateMismatch.heightMm}
        matWmm={appState.materialWidth}
        matHmm={appState.materialHeight}
        onCrop={async () => {
          const { dataUrl, widthMm, heightMm } = await fitTemplateToMaterial(
            templateMismatch.dataUrl, templateMismatch.widthMm, templateMismatch.heightMm,
            appState.materialWidth, appState.materialHeight, true
          );
          setTemplate({ dataUrl, widthMm, heightMm });
          setTemplateMismatch(null);
        }}
        onCancel={() => setTemplateMismatch(null)}
      />
    )}
    {showLayers && (
      <LayersDialog
        layers={layers}
        longSidePx={PICTURE_QUALITY_PX[appState.pictureQuality]}
        onChange={newLayers => { setLayers(newLayers); setIsDirty(true); }}
        onClose={() => setShowLayers(false)}
      />
    )}
    {showTemLayers && (
      <TemLayersDialog
        layers={temLayers}
        longSidePx={PICTURE_QUALITY_PX[appState.pictureQuality]}
        onChange={setTemLayers}
        onUseAsTemplate={async dataUrl => {
          setShowTemLayers(false);
          await handleUseTemplateFromNormalizer(dataUrl, appState.materialWidth, appState.materialHeight);
        }}
        onClose={() => setShowTemLayers(false)}
      />
    )}
    {showTplDesigner && (
      <TemplateDesigner
        longSidePx={PICTURE_QUALITY_PX[appState.pictureQuality]}
        currentTemplateDataUrl={template?.dataUrl ?? null}
        currentTemplateWidthMm={template?.widthMm}
        currentTemplateHeightMm={template?.heightMm}
        onUseAsTemplate={async (dataUrl, widthMm, heightMm) => {
          setShowTplDesigner(false);
          await handleUseTemplateFromNormalizer(dataUrl, widthMm, heightMm);
        }}
        onRunNormalizer={(dataUrl, widthMm, heightMm) => {
          setNormInitTpl({ dataUrl, widthMm, heightMm });
          setShowTplDesigner(false);
          setShowNormalizer(true);
        }}
        onClose={() => setShowTplDesigner(false)}
      />
    )}
    {showNormalizer && (
      <TemplateNormalizerDialog
        onClose={() => setShowNormalizer(false)}
        onUseAsTemplate={async (dataUrl, widthMm, heightMm) => {
          setShowNormalizer(false);
          await handleUseTemplateFromNormalizer(dataUrl, widthMm, heightMm);
        }}
        machineWidthMm={(MACHINE_TYPES.find(m => m.id === appState.machineType) ?? MACHINE_TYPES[0]).widthMm}
        machineHeightMm={(MACHINE_TYPES.find(m => m.id === appState.machineType) ?? MACHINE_TYPES[0]).heightMm}
        initialTemplateDataUrl={normInitTpl?.dataUrl ?? null}
        initialWidthMm={normInitTpl?.widthMm}
        initialHeightMm={normInitTpl?.heightMm}
      />
    )}
    {showParameters && (
      <ParametersDialog
        appState={appState}
        onSave={update => { handleUpdate(update); setIsDirty(true); }}
        onClose={() => setShowParameters(false)}
      />
    )}
    {showTplPreview && template && (
      <TemplatePreviewDialog
        templateDataUrl={template.dataUrl}
        templateWidthMm={template.widthMm}
        templateHeightMm={template.heightMm}
        meshSvg={meshSvg}
        meshStatus={meshStatus}
        meshError={meshError}
        onClose={() => setShowTplPreview(false)}
      />
    )}
    <input
      ref={tplFileRef}
      type="file"
      accept="image/png"
      style={{ display: 'none' }}
      onChange={handleTemplateFile}
    />
    <input
      ref={projFileRef}
      type="file"
      accept=".pdd,.zip"
      style={{ display: 'none' }}
      onChange={handleProjFile}
    />
    <div className="app-root">
      <LeftPanel
        appState={appState}
        currentUser={auth.user}
        templateDataUrl={template?.dataUrl ?? null}
        infoMsg={infoMsg}
        hasCutRegion={!!(triangulation?.triCutLabels?.some(v => v === 1))}
        suggestedMaxH={suggestedMaxH}
        onUpdate={handleUpdate}
        onLogout={handleLogout}
        onLoadTemplate={handleLoadTemplate}
        onNewProj={handleNewProj}
        onLoadProj={handleLoadProj}
        onSaveProj={handleSaveProj}
        onLayers={handleLayers}
        onTemLayers={handleTemLayers}
        onTempDesign={handleTempDesign}
        onParameters={handleParameters}
        onResetView={handleResetView}
        onSave3D={handleSave3D}
        onSave2D={handleSave2D}
        onDistortions={handleDistortions}
        onDistortionReset={handleDistortionReset}
        simState={simState}
        onSimStart={handleSimStart}
        onSimPause={handleSimPause}
        onSimStop={handleSimStop}
        onSimReset={handleSimReset}
        onSimRelax={handleSimRelax}
        simDiagReady={simDiagReady}
        onDownloadSimLog={handleDownloadSimLog}
        simHmReady={!!triangulation}
        onDownloadHeightMap={handleDownloadHeightMap}
        meshReady={!!triangulation}
        onDownloadMesh={handleDownloadMesh}
        onShowPreview={template ? () => setShowTplPreview(true) : undefined}
        onExit={handleExit}
      />
      <Viewport3D
        ref={viewportRef}
        appState={appState}
        textureDataUrl={distortedTextureDataUrl ?? textureDataUrl}
        triangulation={triangulation}
      />
    </div>
    </>
  );
};

export default App;
